import { AlarmLevel } from "@/types/alarm"

export const AlarmNotificationScopes = {
    COMPANY: "COMPANY",
    LOCATION_GROUP: "LOCATION_GROUP",
    LOCATION: "LOCATION",
    DEVICE: "DEVICE",
} as const

export type AlarmNotificationScope =
    (typeof AlarmNotificationScopes)[keyof typeof AlarmNotificationScopes]

export type AlarmSubscriptionType = "INCLUDE" | "EXCLUDE"

export type AlarmNotificationState = AlarmSubscriptionType | "DEFAULT"

export type AlarmNotificationSource =
    | {
          scope: AlarmNotificationScope
          id: number
          type: AlarmSubscriptionType
          subscriptionId: number
      }
    | { scope: "OWNER" }
    | null

export type AlarmNotificationOwnRow = {
    subscriptionId: number
    type: AlarmSubscriptionType
}

export type AlarmNotificationNode = {
    key: string
    parentKey: string | null
    scope: AlarmNotificationScope
    id: number
    name: string
    deviceType: string | null
    deviceCategory: string | null
    subscribed: boolean
    source: AlarmNotificationSource
    inherited: boolean
    own: AlarmNotificationOwnRow | null
    isOwner: boolean
    setBelow: number
    hasChildren: boolean
    match: boolean
}

export type AlarmNotificationEffective = {
    subscribed: boolean
    source: AlarmNotificationSource
    inherited: boolean
}

export type AlarmNotificationTarget = {
    scope: AlarmNotificationScope
    id: number
}

export type AlarmNotificationHeaderTarget = AlarmNotificationTarget & {
    name: string
    icon: string
}

export type AlarmNotificationChange = AlarmNotificationTarget & {
    own: AlarmNotificationOwnRow | null
    state: AlarmNotificationState
}

export type AlarmNotificationFilter = {
    search: string
    onlySet: boolean
    scope: AlarmNotificationScope | null
}

export type AlarmNotificationPage = {
    keys: string[]
    total: number
}

export type AlarmNotificationDelivery = {
    alarmLevels: AlarmLevel[]
    alarmEmailOptIn: boolean
    alarmOkEmailOptIn: boolean
    alarmPushNotificationOptIn: boolean
    alarmOkPushNotificationOptIn: boolean
}

export type AlarmNotificationTreeData = {
    data: { nodes: AlarmNotificationNode[] }
    meta: { pagination: { total: number; limit: number; offset: number } }
}

export const ROOT_KEY = ""

export const TREE_PAGE_SIZE = 25

export const CHILDREN_PAGE_SIZE = 25

export const scopeIdField: Record<AlarmNotificationScope, string> = {
    COMPANY: "companyId",
    LOCATION_GROUP: "locationGroupId",
    LOCATION: "locationId",
    DEVICE: "deviceId",
}

export const scopeIcons: Record<AlarmNotificationScope, string> = {
    COMPANY: "fa-building-columns",
    LOCATION_GROUP: "fa-sitemap",
    LOCATION: "fa-house",
    DEVICE: "fa-microchip",
}

export const deviceCategoryIcons: Record<string, string> = {
    SOLAR_POWER_PLANT: "fa-sun",
    GRID: "fa-plug",
    BATTERY: "fa-battery-three-quarters",
    HYBRID_INVERTER: "fa-solar-panel",
    EV_CHARGER: "fa-car",
    HVAC: "fa-fan",
}

export const nodeIcon = (scope: AlarmNotificationScope, deviceCategory?: string | null): string =>
    deviceCategoryIcons[deviceCategory ?? ""] ?? scopeIcons[scope]

export const nodeKey = (scope: AlarmNotificationScope, id: number): string => `${scope}:${id}`

export const nodeState = (own: AlarmNotificationOwnRow | null): AlarmNotificationState =>
    own ? own.type : "DEFAULT"
